import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TableCell,
  TextField,
  Typography
} from '@mui/material';
import { APIClient } from 'src/helper/api_helper';
import QuoteEditorRateOptions from './QuoteEditorRateOptions';
import { shouldIncludePerDiem, shouldIncludeMiscFees } from './quoteFeeColumns';

export const documentOptions = (document: any) => {
  const saved = document?.clientDocumentOptions ?? {};
  return {
    includeArea: saved.includeArea ?? false,
    includeToolsGear: saved.includeToolsGear ?? false,
    includePerDiem: saved.includePerDiem ?? shouldIncludePerDiem(document),
    includeMiscFees: saved.includeMiscFees ?? shouldIncludeMiscFees(document),
    contractTerms: saved.contractTerms ?? '',
    rateTypesNotes: saved.rateTypesNotes ?? '',
    notes: saved.notes ?? ''
  };
};
export function useDocumentOptions(document: any) {
  const [options, setOptions] = useState(() => documentOptions(document));
  useEffect(() => {
    setOptions(documentOptions(document));
  }, [document?.id, document?.clientDocumentOptions]);
  return { options, setOptions };
}
export function DocumentOptionsEditor({
  kind,
  id,
  options,
  onChange,
  disabled = false
}: any) {
  const [saving, setSaving] = useState(false),
    [message, setMessage] = useState(''),
    [failed, setFailed] = useState(false);
  const change = (key: string, value: any) => {
    onChange({ ...options, [key]: value });
    setMessage('Unsaved document options');
  };
  const save = async () => {
    setSaving(true);
    setMessage('');
    setFailed(false);
    try {
      await new APIClient().create(
        kind === 'actuals'
          ? `/actuals/document-options/${id}`
          : `/jobs/clientInvoice/${id}/document-options`,
        options,
        {}
      );
      setMessage('Document options saved. No email sent.');
    } catch (error) {
      setFailed(true);
      setMessage(
        (error as any)?.message ||
          'Unable to save document options. Your edits remain open.'
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ mx: { xs: 2, md: 6 }, my: 2 }} data-html2canvas-ignore="true">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        gap={2}
      >
        <QuoteEditorRateOptions
          showArea={options.includeArea}
          showToolsGear={options.includeToolsGear}
          includePerDiem={options.includePerDiem}
          includeMiscFees={options.includeMiscFees}
          disabled={disabled || saving}
          onShowAreaChange={(v) => change('includeArea', v)}
          onShowToolsGearChange={(v) => change('includeToolsGear', v)}
          onIncludePerDiemChange={(v) => change('includePerDiem', v)}
          onIncludeMiscFeesChange={(v) => change('includeMiscFees', v)}
        />
        <Button
          variant="contained"
          disabled={disabled || saving || !id}
          onClick={save}
          sx={{ background: '#000', color: '#E8BF21', textTransform: 'none' }}
        >
          Save Document Options
        </Button>
      </Stack>
      <Typography variant="caption">
        Saved for this {kind === 'actuals' ? 'Actuals document' : 'Invoice'}{' '}
        only. Column visibility does not change charges.
      </Typography>
      <Box sx={{ border: '1px solid #dbbe00', mt: 2 }}>
        <Typography
          component="h3"
          sx={{
            m: 0,
            p: 1.25,
            background: '#e9d8a6',
            fontSize: 13,
            fontWeight: 700,
            color: '#000'
          }}
        >
          Contract Terms &amp; Rate Types
        </Typography>
        <Stack spacing={2} sx={{ p: 1.5 }}>
          {[
            ['contractTerms', 'Document Contract Terms'],
            ['rateTypesNotes', 'Rate Types Notes'],
            ['notes', 'Document Notes']
          ].map(([key, label]) => (
            <TextField
              key={key}
              label={label}
              multiline
              minRows={2}
              value={options[key]}
              onChange={(e) => change(key, e.target.value)}
              disabled={disabled || saving}
              inputProps={{ maxLength: 20000 }}
            />
          ))}
        </Stack>
      </Box>
      {message && (
        <Alert severity={failed ? 'error' : 'info'} sx={{ mt: 1 }}>
          {message}
        </Alert>
      )}
    </Box>
  );
}
export function DocumentContractBlock({ options, positions = [] }: any) {
  const rates = Array.from(
    new Set(positions.map((row: any) => row.rateType).filter(Boolean))
  ).join(' · ');
  return (
    <Box
      aria-label="Document contract terms and notes"
      data-document-contract
      sx={{
        mx: { xs: 2, md: 6 },
        my: 2,
        border: '1px solid #dbbe00',
        color: '#000',
        breakInside: 'avoid'
      }}
    >
      <Typography
        component="h3"
        sx={{
          m: 0,
          p: 1.25,
          background: '#e9d8a6',
          fontWeight: 700,
          fontSize: 13
        }}
      >
        Contract Terms &amp; Rate Types
      </Typography>
      <Box
        sx={{
          p: 1.5,
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere'
        }}
      >
        <Typography variant="body2">
          <strong>Rate Types:</strong> {rates || 'No rate types recorded.'}
        </Typography>
        {options.contractTerms && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Contract Terms:</strong>
            {'\n'}
            {options.contractTerms}
          </Typography>
        )}
        {options.rateTypesNotes && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Rate Types Notes:</strong>
            {'\n'}
            {options.rateTypesNotes}
          </Typography>
        )}
        {options.notes && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Notes:</strong>
            {'\n'}
            {options.notes}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
export function DocumentExtraCell({ position, field }: any) {
  return (
    <TableCell
      align="center"
      sx={{
        border: '1px solid #dbbe00',
        fontSize: 13,
        color: '#000',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere'
      }}
    >
      {position?.[field] || position?.Shift?.[field] || '—'}
    </TableCell>
  );
}
export function DocumentColgroup({ options }: any) {
  return (
    <colgroup data-pdf-exclude>
      <col style={{ width: 166 }} />
      <col style={{ width: 64 }} />
      {options.includeArea && <col style={{ width: 90 }} />}
      <col style={{ width: 150 }} />
      {options.includeToolsGear && <col style={{ width: 100 }} />}
      <col style={{ width: 222 }} />
      <col />
      {[80, 80, 80, 75, 60, 60, 60].map((width, i) => (
        <col key={i} style={{ width }} />
      ))}
      {options.includePerDiem && <col style={{ width: 80 }} />}
      {options.includeMiscFees && <col style={{ width: 80 }} />}
      <col style={{ width: 95 }} />
    </colgroup>
  );
}
export const documentTableSx = (options: any) => ({
  tableLayout: 'fixed',
  minWidth: 1470 + Number(options.includeArea) * 90 + Number(options.includeToolsGear) * 100 + Number(options.includePerDiem) * 80 + Number(options.includeMiscFees) * 80,
  '& th, & td': {
    borderWidth: '1px !important',
    borderColor: '#dbbe00 !important'
  },
  [`& tbody tr > td:nth-of-type(${5 + Number(options.includeArea) + Number(options.includeToolsGear)})`]: { whiteSpace: 'normal !important', overflowWrap: 'anywhere', textAlign: 'left' },
  '& thead th': { width: 'auto !important', minWidth: '0 !important', textTransform: 'none' }
});
